/**
 * Death in two times (GDD section 6): a death burst when the unit falls, then a stele whose legacy generates
 * the unit's material at the start of each of the next 3 turns. Built for all of them; Zorg leaves no trace.
 */
import { Physique, STATE_LIQUID, nextInt, wakeRect } from '@invaders/sim';
import { FP_SHIFT, type GameState, type Stele, type Unit } from './state';
import { BARBE_NOIRE, CLOUDMAN, FIREMAN, FISHMAN, GOLEM, LAVAMAN, LIZARDMAN, ROBOTMAN, SANDMAN, SNOWMAN, TREEMAN, ZORG, INVADERS } from './invaders';
import { discharge, freezeCells } from './cold';
import { explode } from './explosion';
import { UNIT_H, UNIT_W } from './unit';

const { EMPTY, ROCK, SAND, WATER, LAVA, WOOD, STEAM, FIRE, BLACK_POWDER, ACID, ICE } = Physique;

/** Number of turns a legacy lasts. */
export const LEGACY_TURNS = 3;

/** Kinds that leave a stele (with a legacy built in this wave). */
const WITH_LEGACY = new Set([GOLEM, SANDMAN, FISHMAN, LAVAMAN, TREEMAN, CLOUDMAN, FIREMAN, BARBE_NOIRE, LIZARDMAN, SNOWMAN, ROBOTMAN]);

/** Damage of an electric discharge: 15 in the GDD (the electric Wave), tripled by the author. */
export const DISCHARGE_DAMAGE = 45;

/** GDD: Fireman bursts into a ball of fire of radius 10 that sets the units around on fire. */
export const FIREBALL_RADIUS = 10;
/** GDD: Barbe Noire's powder blows up with a radius of 12. Damage not in the GDD: tuning value. */
export const SAINTE_BARBE_RADIUS = 12;
export const SAINTE_BARBE_DAMAGE = 30;

function insideUnit(state: GameState, x: number, y: number): boolean {
  for (const u of state.units) {
    if (!u.alive) continue;
    const ux = u.x >> FP_SHIFT;
    const uy = u.y >> FP_SHIFT;
    if (x >= ux && x < ux + UNIT_W && y >= uy && y < uy + UNIT_H) return true;
  }
  return false;
}

/** True if a generated cell may go to (x, y): an empty or gas cell (liquids too if `overLiquids`), never in a unit. */
function free(state: GameState, x: number, y: number, overLiquids: boolean): boolean {
  const { w, h, data } = state.world.physique;
  if (x < 0 || x >= w || y < 0 || y >= h) return false;
  const s = state.world.rules.materials.state[data[y * w + x]];
  return (s > STATE_LIQUID || (overLiquids && s === STATE_LIQUID) || data[y * w + x] === EMPTY) && !insideUnit(state, x, y);
}

function put(state: GameState, x: number, y: number, mat: number): void {
  const { w } = state.world.physique;
  state.world.physique.data[y * w + x] = mat;
  state.world.cellState.data[y * w + x] = 0;
}

/** Fills a disc with `mat` in the free cells. */
function disc(state: GameState, cx: number, cy: number, r: number, mat: number, overLiquids = false): void {
  for (let y = cy - r; y <= cy + r; y++) {
    for (let x = cx - r; x <= cx + r; x++) {
      if ((x - cx) * (x - cx) + (y - cy) * (y - cy) <= r * r && free(state, x, y, overLiquids)) put(state, x, y, mat);
    }
  }
  wakeRect(state.world, cx - r, cy - r, cx + r, cy + r);
}

/** Puts `n` cells of `mat` as a compact blob centred on (cx, cy): rings of growing radius, free cells only. */
function blob(state: GameState, cx: number, cy: number, n: number, mat: number): void {
  let placed = 0;
  for (let r = 0; r <= 24 && placed < n; r++) {
    for (let y = cy - r; y <= cy + r && placed < n; y++) {
      for (let x = cx - r; x <= cx + r && placed < n; x++) {
        const d2 = (x - cx) * (x - cx) + (y - cy) * (y - cy);
        if (d2 > r * r || d2 <= (r - 1) * (r - 1) || !free(state, x, y, false)) continue;
        put(state, x, y, mat);
        placed++;
      }
    }
  }
  wakeRect(state.world, cx - 24, cy - 24, cx + 24, cy + 24);
}

/** A column of `mat` rising from the ground at the stele: `width` cells wide, up to `height` cells high. */
function column(state: GameState, s: Stele, width: number, height: number, mat: number): void {
  const x0 = s.x - (width >> 1);
  for (let y = s.y; y > s.y - height; y--) for (let x = x0; x < x0 + width; x++) if (free(state, x, y, true)) put(state, x, y, mat);
  wakeRect(state.world, x0, s.y - height, x0 + width, s.y);
}

/** Feet of the unit: centre column, last row of the body. */
function feet(u: Unit): [number, number] {
  return [(u.x >> FP_SHIFT) + (UNIT_W >> 1), (u.y >> FP_SHIFT) + UNIT_H - 1];
}

/** The burst of a unit that just fell. Returns true if it leaves a stele. */
function deathBurst(state: GameState, u: Unit): boolean {
  const [fx, fy] = feet(u);
  const cy = fy - (UNIT_H >> 1);
  switch (u.kind) {
    case ZORG: // the ray of his saucer takes him away: no trace
      return false;
    case GOLEM: { // breaks into blocks of rock within a radius of 5
      const rng = state.world.rng;
      for (let i = 0; i < 7; i++) {
        const bx = fx - 5 + nextInt(rng, 10);
        const by = cy - 2 + nextInt(rng, 8);
        for (let y = by; y < by + 3; y++) for (let x = bx; x < bx + 3; x++) if (free(state, x, y, true)) put(state, x, y, ROCK);
      }
      wakeRect(state.world, fx - 8, cy - 8, fx + 8, cy + 12);
      return true;
    }
    case SANDMAN: disc(state, fx, cy, 7, SAND); return true; // collapses into a mound of sand
    case FISHMAN: disc(state, fx, cy, 8, WATER); return true; // bursts into a wave of water
    case LAVAMAN: disc(state, fx, cy - 2, 6, LAVA); return true; // a geyser of lava
    case TREEMAN: // sets into a wooden stump
      for (let y = fy; y > fy - 8; y--) for (let x = fx - 4; x < fx + 4; x++) if (free(state, x, y, true)) put(state, x, y, WOOD);
      wakeRect(state.world, fx - 5, fy - 9, fx + 5, fy + 1);
      return true;
    case CLOUDMAN: disc(state, fx, cy - 4, 10, STEAM); return true; // the jar breaks: a cloud of steam
    case FIREMAN: { // bursts into a ball of fire that sets the units around on fire
      disc(state, fx, cy, FIREBALL_RADIUS, FIRE);
      for (const o of state.units) {
        if (!o.alive || INVADERS[o.kind].fire === 0) continue;
        const ox = (o.x >> FP_SHIFT) + (UNIT_W >> 1) - fx;
        const oy = (o.y >> FP_SHIFT) + (UNIT_H >> 1) - cy;
        if (ox * ox + oy * oy <= (FIREBALL_RADIUS + (UNIT_W >> 1)) * (FIREBALL_RADIUS + (UNIT_W >> 1))) o.burning = true;
      }
      return true;
    }
    case BARBE_NOIRE: // Sainte-Barbe: his powder blows up (his tricorn flies away: client side)
      explode(state, fx, cy, SAINTE_BARBE_RADIUS, SAINTE_BARBE_DAMAGE);
      return true;
    case LIZARDMAN: disc(state, fx, fy - 2, 5, ACID); return true; // melts into a puddle of acid
    case SNOWMAN: { // Grand gel: a block of ice locks him in and the water freezes within 16
      const x0 = u.x >> FP_SHIFT;
      const y0 = u.y >> FP_SHIFT;
      for (let y = y0 - 1; y <= y0 + UNIT_H - 1; y++) for (let x = x0 - 1; x <= x0 + UNIT_W; x++) if (free(state, x, y, true)) put(state, x, y, ICE);
      freezeCells(state, fx, cy, 16);
      return true;
    }
    case ROBOTMAN: discharge(state, fx, cy, 6, DISCHARGE_DAMAGE); return true; // bursts into sparks
    default:
      return WITH_LEGACY.has(u.kind);
  }
}

/** Death bursts and steles of the units that died during this tick (read from the events). */
export function processDeaths(state: GameState): void {
  // A burst may kill more units (Barbe Noire's explosion): their deaths are appended and handled in turn.
  for (let k = 0; k < state.events.length; k++) {
    const e = state.events[k];
    if (e.type !== 'death' || e.offMap) continue;
    const u = state.units[e.unit];
    const [fx, fy] = feet(u);
    state.events.push({ type: 'burst', unit: u.id, kind: u.kind, x: fx, y: fy - (UNIT_H >> 1) });
    if (deathBurst(state, u) && WITH_LEGACY.has(u.kind)) state.steles.push({ x: fx, y: fy, kind: u.kind, player: u.player, stage: 0 });
  }
}

/** Steles fall onto the ground (the terrain under them may have been blown away). */
export function updateSteles(state: GameState): void {
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  for (let i = state.steles.length - 1; i >= 0; i--) {
    const s = state.steles[i];
    if (s.y + 1 >= h) { state.steles.splice(i, 1); continue; }
    if (matState[data[(s.y + 1) * w + s.x]] >= STATE_LIQUID) s.y++;
  }
}

/** A direct explosion on a stele cancels the rest of its legacy. */
export function blastSteles(state: GameState, cx: number, cy: number, radius: number): void {
  for (let i = state.steles.length - 1; i >= 0; i--) {
    const s = state.steles[i];
    const dx = s.x - cx;
    const dy = s.y - 4 - cy;
    if (dx * dx + dy * dy > radius * radius) continue;
    state.events.push({ type: 'legacy', kind: s.kind, x: s.x, y: s.y, stage: 0 });
    state.steles.splice(i, 1);
  }
}

/** GDD section 3, step 1: the active legacies fire, then their counter goes down. */
export function triggerLegacies(state: GameState): void {
  for (let i = state.steles.length - 1; i >= 0; i--) {
    const s = state.steles[i];
    s.stage++;
    const stage = s.stage;
    switch (s.kind) {
      case GOLEM: column(state, s, 6, 4 * stage, ROCK); break; // a pillar: 4, 8, then 12 cells, a permanent wall
      case SANDMAN: blob(state, s.x, s.y - 14, 120, SAND); break; // 120 cells of sand each turn
      case FISHMAN: blob(state, s.x, s.y - 8, 150, WATER); break; // a spring: 150 cells of water
      case LAVAMAN: blob(state, s.x, s.y - 6, 80, LAVA); break; // 80 cells of lava
      case TREEMAN: tree(state, s, stage); break;
      case CLOUDMAN: blob(state, s.x, s.y - 40, 30 + 30 * stage, STEAM); break; // 60, 90, 120 cells of steam
      case FIREMAN: fireLegacy(state, s, stage); break;
      case BARBE_NOIRE: blob(state, s.x, s.y - 6, 40, BLACK_POWDER); break; // 40 cells of powder: a trap
      case LIZARDMAN: blob(state, s.x, s.y - 4, 60, ACID); break; // 60 cells of acid: the ground gives way
      case SNOWMAN: iceLegacy(state, s, 2 + 4 * stage); break; // ice within 6, 10, then 14: the lake becomes a bridge
      case ROBOTMAN: discharge(state, s.x, s.y - 4, 4 + 2 * stage, DISCHARGE_DAMAGE); break; // 6, 8, 10, then it goes out
    }
    state.events.push({ type: 'legacy', kind: s.kind, x: s.x, y: s.y, stage });
    if (stage >= LEGACY_TURNS) state.steles.splice(i, 1);
  }
}

/** Treeman's legacy: a trunk of 6 cells, then 12 with branches, then a whole tree (a bridge or a cover that burns). */
function tree(state: GameState, s: Stele, stage: number): void {
  const height = stage === 1 ? 6 : stage === 2 ? 12 : 20;
  column(state, s, 4, height, WOOD);
  const branch = (y: number, len: number) => {
    for (let x = s.x - len; x <= s.x + len; x++) if (free(state, x, y, true)) put(state, x, y, WOOD);
  };
  if (stage >= 2) { branch(s.y - 10, 6); branch(s.y - 11, 4); }
  if (stage >= 3) { branch(s.y - 16, 10); branch(s.y - 17, 8); branch(s.y - 19, 5); }
  wakeRect(state.world, s.x - 12, s.y - 22, s.x + 12, s.y);
}

/** Fireman's legacy: 60 cells of fire, then 40 and embers thrown around, then 20 and a plume. */
function fireLegacy(state: GameState, s: Stele, stage: number): void {
  blob(state, s.x, s.y - 4, stage === 1 ? 60 : stage === 2 ? 40 : 20, FIRE);
  const rng = state.world.rng;
  if (stage === 2) {
    for (let i = 0; i < 12; i++) {
      const x = s.x - 20 + nextInt(rng, 41);
      const y = s.y - 4 - nextInt(rng, 16);
      if (free(state, x, y, false)) put(state, x, y, FIRE);
    }
  }
  if (stage === 3) for (let y = s.y - 6; y > s.y - 30; y -= 2) if (free(state, s.x, y, false)) put(state, s.x, y, FIRE);
  wakeRect(state.world, s.x - 24, s.y - 32, s.x + 24, s.y);
}

/** Snowman's legacy: the water within r freezes, and the ground gets a crust of ice. */
function iceLegacy(state: GameState, s: Stele, r: number): void {
  freezeCells(state, s.x, s.y, r);
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  for (let y = Math.max(0, s.y - r); y <= Math.min(h - 2, s.y + r); y++) {
    for (let x = Math.max(0, s.x - r); x <= Math.min(w - 1, s.x + r); x++) {
      if ((x - s.x) * (x - s.x) + (y - s.y) * (y - s.y) > r * r) continue;
      if (free(state, x, y, false) && matState[data[(y + 1) * w + x]] < STATE_LIQUID) put(state, x, y, ICE);
    }
  }
  wakeRect(state.world, s.x - r, s.y - r, s.x + r, s.y + r);
}
